import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { X, ImagePlus, Smile, Mic, Send, Loader2, Pencil, RotateCw, AlertTriangle, Trash2, Pause } from "lucide-react";
import { api } from "@/lib/api";
import { hapticLight } from "@/lib/haptics";
import { uploadMediaFile } from "@/components/FileUpload";
import { toast } from "sonner";
import { EmojiTabsGrid } from "@/components/EmojiTabsGrid";
import { QuoteThumb, quoteLabel } from "./QuoteThumb";
import { RefCard } from "./RefCard";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { keepFocusTap } from "@/lib/keepFocusTap";
import { ChatTextarea } from "./ChatTextarea";
import { dmDraftKey, getChatDraft, setChatDraft, clearChatDraft, syncChatDrafts, flushChatDrafts } from "@/lib/chatDrafts";
import { profileGateProps } from "@/lib/profileDone";

const DEFAULT_VOICE_MAX_SECONDS = 60; // durée max d'un vocal (réglable Admin › Messages : settings.chat.voice_max_seconds)
const REC_BARS = [10, 16, 22, 14, 26, 18, 12, 24, 16, 28, 14, 20, 10, 24, 16, 20, 12, 26, 18, 14, 22, 12, 20, 26];
const fmtRec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const videoDuration = (file) => new Promise((res) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { URL.revokeObjectURL(v.src); res(v.duration || 0); };
  v.onerror = () => { URL.revokeObjectURL(v.src); res(0); };
  v.src = URL.createObjectURL(file);
});

// Capture the first frame of a video as a JPEG data URL → used as <video poster>
// so the chat preview shows the real frame instead of the WebView's grey placeholder.
const videoPoster = (file) => new Promise((res) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.muted = true;
  v.playsInline = true;
  const url = URL.createObjectURL(file);
  let done = false;
  const finish = (val) => { if (done) return; done = true; try { URL.revokeObjectURL(url); } catch { /* noop */ } res(val); };
  const grab = () => {
    try {
      const w = v.videoWidth || 360, h = v.videoHeight || 640;
      // Downscale the poster canvas — drawing a full-res (1080p/4K) frame + encoding it
      // OOM-crashes the Android WebView. Cap the longest side to keep memory tiny.
      const MAX = 480;
      const scale = Math.min(1, MAX / Math.max(w, h));
      const cw = Math.max(1, Math.round(w * scale));
      const ch = Math.max(1, Math.round(h * scale));
      const c = document.createElement("canvas");
      c.width = cw; c.height = ch;
      c.getContext("2d").drawImage(v, 0, 0, cw, ch);
      finish(c.toDataURL("image/jpeg", 0.7));
    } catch { finish(null); }
  };
  v.onloadeddata = () => { try { v.currentTime = Math.min(0.1, (v.duration || 1) / 2); } catch { grab(); } };
  v.onseeked = grab;
  v.onerror = () => finish(null);
  setTimeout(() => { if (!done) grab(); }, 1200);
  v.src = url;
});

// Chat composer: text + emoji + attachments (WhatsApp album/caption screen) + voice notes,
// with optimistic send. Owns all composer state; exposes startReply/startEdit to the parent.
export const Composer = forwardRef(({ user, chatTarget, convId, setConvId, setMessages, config, t, isMobile, wsRef, outbox }, ref) => {
  const [text, setText] = useState("");
  const [media, setMedia] = useState(null); // {url, media_type, preview} — audio (voice note) only
  const [attachments, setAttachments] = useState([]); // album: [{id,url,media_type,preview,uploading}]
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [micOnRec, setMicOnRec] = useState(false);
  const [recPaused, setRecPaused] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const voiceMax = Number(config?.settings?.chat?.voice_max_seconds) || DEFAULT_VOICE_MAX_SECONDS;
  const [captionIndex, setCaptionIndex] = useState(0);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  // Context card (Marketplace listing…) attached to the NEXT message sent — WhatsApp Business-style.
  const [pendingRef, setPendingRef] = useState(null);
  const [editMsg, setEditMsg] = useState(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const recorderRef = useRef(null);
  const sendAfterRef = useRef(false);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const recSecsRef = useRef(0);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const uploadPromises = useRef(new Map());
  const uploadProgress = useRef(new Map()); // attId -> percent (0-100)
  const attToMsg = useRef(new Map());       // attId -> optimistic message id
  const msgAtts = useRef(new Map());        // message id -> [attId]
  const retryStore = useRef(new Map());     // message id -> delivery job (for "Retry")
  const lastTypingSent = useRef(0);

  // Average upload % across a message's attachments → drives the in-bubble progress ring.
  const syncMsgProgress = (msgId) => {
    const ids = msgAtts.current.get(msgId) || [];
    if (!ids.length) return;
    const vals = ids.map((id) => uploadProgress.current.get(id) ?? 0);
    const pct = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
    setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, _uploadPct: pct } : x)));
    outbox?.patch?.(msgId, { _uploadPct: pct });
  };

  // Fresh composer whenever the conversation changes. A draft (the ad's headline,
  // set when chat is opened from an ad's "Send a message" CTA) pre-fills the input ;
  // sinon le brouillon gardé pour cette conversation sur cet appareil (lot 568, façon WhatsApp).
  const draftKey = dmDraftKey(chatTarget?.id);
  useEffect(() => {
    const key = dmDraftKey(chatTarget?.id);
    const draft = chatTarget?._draft || getChatDraft(key);
    setText(draft); setMedia(null); setAttachments([]); setCaptionIndex(0);
    setReplyTo(null); setEditMsg(null); setEmojiOpen(false); setConfirmClose(false);
    setPendingRef(chatTarget?._ref || null);
    if (draft || chatTarget?._ref) setTimeout(() => inputRef.current?.focus(), 60);
    // Lot 569 : copie serveur (autre appareil) — appliquée seulement si on n'a pas commencé à taper entre-temps.
    let alive = true;
    if (key && !chatTarget?._draft) {
      syncChatDrafts().then((all) => {
        if (!alive || !all) return;
        const remote = all[key]?.text || "";
        if (remote !== draft) setText((cur) => (cur === draft ? remote : cur));
      });
    }
    return () => { alive = false; flushChatDrafts(); };
  }, [chatTarget?.id, chatTarget?._draft, chatTarget?._ref]);
  // Texte tapé (hors modification d'un message) → brouillon de la conversation.
  const typeText = (next) => { setText(next); if (!editMsg) setChatDraft(draftKey, next); };

  const discardCaption = () => { setConfirmClose(false); setAttachments([]); setCaptionIndex(0); };
  // Don't silently lose a failed attachment when closing the caption sheet — confirm first.
  const requestCaptionClose = () => { if (attachments.some((a) => a.failed)) setConfirmClose(true); else discardCaption(); };

  useImperativeHandle(ref, () => ({
    startReply: (m) => { setEditMsg(null); setReplyTo(m); setTimeout(() => inputRef.current?.focus(), 0); },  // lot 727 — clavier prêt après le glissé
    startEdit: (m) => {
      setReplyTo(null); setMedia(null); setAttachments([]);
      setEditMsg(m); setText(m.content || "");
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    resend: (msg) => {
      const job = retryStore.current.get(msg.id) || outbox?.getJob?.(msg.id);
      if (job) { retryStore.current.set(msg.id, job); deliver(msg.id, job); }
    },
    sendEmoji: (emoji) => sendQuick(emoji),  // lot 730 — « En grand » : l'emoji part seul, affiché géant
  }));

  // Envoi immédiat d'un texte sans toucher au brouillon ni aux pièces jointes (même pipeline optimiste / reprise).
  const sendQuick = (content) => {
    if (!content || editMsg) return;
    const tempMsgId = "tmp-" + Math.random().toString(36).slice(2);
    const optimistic = { id: tempMsgId, pending: true, sender_id: user.id, content, created_at: new Date().toISOString(), _poster: null, reply_to: null, ref: null };
    setMessages((m) => [...m, optimistic]);
    outbox?.add?.(optimistic);
    hapticLight();
    const job = { content, reply: null, media: null, ref: null, atts: [] };
    retryStore.current.set(tempMsgId, job);
    outbox?.setJob?.(tempMsgId, job);
    deliver(tempMsgId, job);
  };

  const cancelEdit = () => { setEditMsg(null); setText(getChatDraft(draftKey)); }; // le brouillon mis de côté revient
  const removeAttachment = (id) => setAttachments((x) => x.filter((y) => y.id !== id));
  const addEmoji = (e) => { typeText(text + e); inputRef.current?.focus(); };

  // Uploads one composer attachment in the background. On success stores its remote URL;
  // on failure (still in the composer) keeps the thumbnail with an error state so the send
  // never silently downgrades to text-only — a per-thumbnail Retry re-runs the upload.
  const startAttachmentUpload = (tempId, file, isImage) => {
    const mediaType = isImage ? "image" : "video";
    setAttachments((a) => a.map((x) => (x.id === tempId ? { ...x, uploading: true, failed: false, progress: 0 } : x)));
    const p = (async () => {
      const toSend = file;
      const data = await uploadMediaFile(toSend, {
        config,
        context: "chat",
        onProgress: (pr) => {
          uploadProgress.current.set(tempId, pr);
          setAttachments((a) => a.map((x) => (x.id === tempId ? { ...x, progress: pr } : x)));
          const mid = attToMsg.current.get(tempId);
          if (mid) syncMsgProgress(mid);
        },
      });
      setAttachments((a) => a.map((x) => (x.id === tempId ? { ...x, url: data.url, uploading: false, failed: false, progress: 100 } : x)));
      return { url: data.url, media_type: mediaType };
    })();
    p.catch((err) => {
      // Already attached to a sent bubble → deliver() handles it (Retry on the bubble).
      if (attToMsg.current.get(tempId)) return;
      // Still in the composer/caption sheet: keep the thumbnail with an error state + Retry.
      toast.error(err?.response?.data?.detail || err?.friendlyMessage || t("chat.file_fail", "Échec de l'envoi du fichier"));
      uploadPromises.current.delete(tempId);
      setAttachments((a) => a.map((x) => (x.id === tempId ? { ...x, uploading: false, failed: true } : x)));
    });
    uploadPromises.current.set(tempId, p);
  };

  const retryAttachment = (att) => { if (att?.file) startAttachmentUpload(att.id, att.file, att.media_type === "image"); };

  const pickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const ml = config?.settings?.media_limits || {};
    const videoEnabled = ml.chat_video_enabled !== false;
    const maxAlbum = Number(ml.chat_album_max) || 10;
    const room = maxAlbum - attachments.length;
    if (room <= 0) { toast.error(t("chat.album_max", `Maximum ${maxAlbum} fichiers par envoi`).replace("{n}", maxAlbum)); return; }
    if (files.length > room) toast.error(t("chat.album_max", `Maximum ${maxAlbum} fichiers par envoi`).replace("{n}", maxAlbum));
    const picked = files.slice(0, room);
    const jobs = [];
    const placeholders = [];
    for (const file of picked) {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (!isVideo && !isImage) { toast.error(t("chat.only_media", "Seules les images et vidéos sont acceptées")); continue; }
      if (isVideo && !videoEnabled) { toast.error(t("chat.video_disabled", "L'envoi de vidéos est temporairement désactivé.")); continue; }
      if (isVideo) {
        const maxSec = Number(ml.chat_video_max_seconds) || 60;
        const maxMb = Number(ml.chat_video_max_mb) || 50;
        if (file.size > maxMb * 1024 * 1024) { toast.error(t("chat.video_too_big", `Vidéo trop lourde (max ${maxMb} Mo).`).replace("{n}", maxMb)); continue; }
        const dur = await videoDuration(file);
        if (dur > maxSec) { toast.error(t("chat.video_too_long", `Vidéo trop longue (max ${maxSec}s)`).replace("{n}", maxSec)); continue; }
      }
      const tempId = Math.random().toString(36).slice(2);
      const poster = isVideo ? await videoPoster(file) : null;
      placeholders.push({ id: tempId, file, url: null, media_type: isVideo ? "video" : "image", preview: URL.createObjectURL(file), poster, uploading: true });
      jobs.push({ tempId, file, isImage });
    }
    if (!placeholders.length) return;
    setAttachments((a) => [...a, ...placeholders]);
    jobs.forEach(({ tempId, file, isImage }) => startAttachmentUpload(tempId, file, isImage));
  };

  const send = async () => {
    const content = text.trim();
    if (editMsg) {
      if (!content || sending) return;
      setSending(true);
      try {
        await api.patch(`/chat/messages/${editMsg.id}`, { content });
        setMessages((ms) => ms.map((x) => (x.id === editMsg.id ? { ...x, content, edited: true } : x)));
        setEditMsg(null); setText(getChatDraft(draftKey));
      } catch (err) {
        toast.error(err?.response?.data?.detail || t("chat.edit_fail", "Modification impossible"));
      } finally { setSending(false); }
      return;
    }
    const localAtts = attachments;
    const localMedia = media;
    const localReply = replyTo;
    const localRef = pendingRef;
    if (!content && localAtts.length === 0 && !localMedia) return;

    // Optimistic (WhatsApp-style): free the composer instantly and show the bubble now;
    // uploads finish in the background, then the message is POSTed and reconciled.
    setText(""); setMedia(null); setAttachments([]); setCaptionIndex(0); setReplyTo(null); setPendingRef(null);
    clearChatDraft(draftKey);

    const tempMsgId = "tmp-" + Math.random().toString(36).slice(2);
    let pv = {};
    let poster = null;
    if (localMedia) pv = { media_url: localMedia.preview || localMedia.url, media_type: localMedia.media_type };
    else if (localAtts.length === 1) { pv = { media_url: localAtts[0].preview, media_type: localAtts[0].media_type }; poster = localAtts[0].poster; }
    else if (localAtts.length > 1) pv = { media_url: localAtts[0].preview, media_type: "album", media_group: localAtts.map((a) => ({ url: a.preview, type: a.media_type })) };
    const optimistic = {
      id: tempMsgId, pending: true, sender_id: user.id, content,
      created_at: new Date().toISOString(),
      _poster: poster,
      reply_to: localReply ? { id: localReply.id, sender_id: localReply.sender_id, content: (localReply.content || "").slice(0, 120), media_type: localReply.media_type, media_url: localReply.media_url } : null,
      ref: localRef || null,
      ...pv,
    };
    setMessages((m) => [...m, optimistic]);
    outbox?.add?.(optimistic);
    hapticLight();

    const job = {
      content, reply: localReply, media: localMedia, ref: localRef,
      atts: localAtts.map((a) => ({ id: a.id, file: a.file, url: a.url, media_type: a.media_type })),
    };
    const attIds = job.atts.map((a) => a.id);
    if (attIds.length) {
      msgAtts.current.set(tempMsgId, attIds);
      attIds.forEach((id) => attToMsg.current.set(id, tempMsgId));
      syncMsgProgress(tempMsgId);
    }
    retryStore.current.set(tempMsgId, job);
    outbox?.setJob?.(tempMsgId, job);
    deliver(tempMsgId, job);
  };

  // Uploads a message's attachments (reusing the in-flight upload, else re-uploading the
  // File), then POSTs it. Reused by first send AND by the "Retry" button on failures.
  const deliver = async (msgId, job) => {
    setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, pending: true, failed: false } : x)));
    try {
      const ready = [];
      for (const a of job.atts) {
        if (a.url) { uploadProgress.current.set(a.id, 100); syncMsgProgress(msgId); ready.push({ url: a.url, media_type: a.media_type }); continue; }
        let res;
        const inflight = uploadPromises.current.get(a.id);
        if (inflight) res = await inflight;
        else {
          res = await uploadMediaFile(a.file, {
            config, context: "chat",
            onProgress: (pr) => { uploadProgress.current.set(a.id, pr); syncMsgProgress(msgId); },
          });
        }
        a.url = res.url;
        uploadProgress.current.set(a.id, 100);
        syncMsgProgress(msgId);
        ready.push({ url: res.url, media_type: a.media_type });
      }
      const payload = { content: job.content, reply_to: job.reply?.id || null };
      if (job.ref?.type && job.ref?.id) payload.ref = { type: job.ref.type, id: job.ref.id };
      if (job.duration) payload.duration = job.duration;
      if (job.media) { payload.media_url = job.media.url; payload.media_type = job.media.media_type; }
      else if (ready.length === 1) { payload.media_url = ready[0].url; payload.media_type = ready[0].media_type; }
      else if (ready.length > 1) { payload.media_group = ready.map((r) => ({ url: r.url, type: r.media_type })); }
      const { data } = await api.post("/chat/messages", convId ? { conversation_id: convId, ...payload } : { recipient_id: chatTarget.id, ...payload });
      if (!convId && data.conversation_id) setConvId(data.conversation_id);
      // Let the inbox / badges refresh: a brand-new conversation must show up on « back ».
      window.dispatchEvent(new CustomEvent("yalola:chat", { detail: { type: "sent", conversation_id: data.conversation_id, request_pending: !!data.request_pending } }));
      setMessages((m) => m.map((x) => (x.id === msgId
        ? { ...data.message, _localPreview: x.media_url, _localGroup: x.media_group }
        : x)));
      retryStore.current.delete(msgId);
      outbox?.remove?.(msgId);
      (msgAtts.current.get(msgId) || []).forEach((id) => { uploadPromises.current.delete(id); attToMsg.current.delete(id); uploadProgress.current.delete(id); });
      msgAtts.current.delete(msgId);
    } catch (err) {
      (job.atts || []).forEach((a) => uploadPromises.current.delete(a.id));
      setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, pending: false, failed: true, _uploadPct: undefined } : x)));
      outbox?.patch?.(msgId, { pending: false, failed: true, _uploadPct: undefined });
      toast.error(err?.response?.data?.detail || t("chat.not_sent", "Message non envoyé"));
    }
  };

  const startRecTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRecSecs((s) => {
        const nx = s + 1;
        if (nx >= voiceMax) { recSecsRef.current = voiceMax; finishRecording(true); return s; }
        recSecsRef.current = nx;
        return nx;
      });
    }, 1000);
  };
  // WhatsApp-style voice note: the bubble is sent optimistically right away and uploaded in
  // the background (same attachment pipeline). No raw <audio> preview — that showed a bogus
  // duration (e.g. "52:41") because a headerless WebM blob reports Infinity to <audio controls>.
  const sendVoiceNote = (file, blobUrl, duration = 0) => {
    const attId = "va-" + Math.random().toString(36).slice(2);
    const tempMsgId = "tmp-" + Math.random().toString(36).slice(2);
    const localReply = replyTo; setReplyTo(null);
    const optimistic = {
      id: tempMsgId, pending: true, sender_id: user.id, content: "",
      created_at: new Date().toISOString(),
      media_url: blobUrl, media_type: "audio", duration: duration || undefined,
      reply_to: localReply ? { id: localReply.id, sender_id: localReply.sender_id, content: (localReply.content || "").slice(0, 120), media_type: localReply.media_type, media_url: localReply.media_url } : null,
    };
    setMessages((m) => [...m, optimistic]);
    outbox?.add?.(optimistic);
    hapticLight();
    const job = { content: "", reply: localReply, media: null, duration: duration || 0, atts: [{ id: attId, file, media_type: "audio" }] };
    msgAtts.current.set(tempMsgId, [attId]);
    attToMsg.current.set(attId, tempMsgId);
    retryStore.current.set(tempMsgId, job);
    outbox?.setJob?.(tempMsgId, job);
    deliver(tempMsgId, job);
  };
  // Stop the recorder; onstop then sends (send=true) or silently discards (trash).
  const finishRecording = (send) => {
    sendAfterRef.current = !!send;
    clearInterval(timerRef.current);
    const rec = recorderRef.current;
    // Resume a paused recorder before stopping so the final chunk actually flushes
    // (several mobile browsers drop the last data when stopped straight from "paused").
    try { if (rec && rec.state === "paused") rec.resume(); } catch { /* noop */ }
    try { rec?.stop(); } catch { /* noop */ }
    setMicOnRec(false); setRecPaused(false);
  };
  const cancelRecording = () => finishRecording(false);
  const togglePauseRecording = () => {
    const rec = recorderRef.current; if (!rec) return;
    if (rec.state === "recording") { try { rec.pause(); } catch { /* noop */ } setRecPaused(true); clearInterval(timerRef.current); }
    else if (rec.state === "paused") { try { rec.resume(); } catch { /* noop */ } setRecPaused(false); startRecTimer(); }
  };
  const startRecording = async () => {
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch { toast.error(t("chat.mic_denied", "Micro refusé. Autorise l'accès pour envoyer un vocal.")); return; }
    chunksRef.current = [];
    sendAfterRef.current = false;
    const rec = new MediaRecorder(stream);
    recorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      stream.getTracks().forEach((tr) => tr.stop());
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
      const doSend = sendAfterRef.current; sendAfterRef.current = false;
      const durationSec = recSecsRef.current || 0;
      recSecsRef.current = 0;
      setRecSecs(0);
      if (!doSend) return;
      if (blob.size < 1000) { toast.error(t("chat.rec_too_short", "Enregistrement trop court, réessaie.")); return; }
      const file = new File([blob], "vocal.webm", { type: blob.type });
      sendVoiceNote(file, URL.createObjectURL(blob), durationSec);
    };
    rec.start(1000);
    setRecSecs(0); setRecPaused(false); setMicOnRec(true);
    startRecTimer();
  };

  const capIdx = attachments.length ? Math.min(captionIndex, attachments.length - 1) : 0;
  const capCur = attachments[capIdx];

  return (
    <>
      {uploading && (
        <div className="px-3 pt-2 bg-white flex items-center gap-2" data-testid="chat-uploading-bar">
          <Loader2 size={14} className="animate-spin" style={{ color: "var(--yalola-primary)" }} />
          <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{t("chat.uploading", "Envoi…")}</span>
        </div>
      )}
      {pendingRef && !editMsg && (
        <div className="px-2 pt-2 bg-white flex items-stretch gap-2" data-testid="chat-ref-preview">
          <div className="flex-1 min-w-0">
            <RefCard card={pendingRef} mine={false} user={user} chatTargetName={chatTarget?.name} t={t} compact size={36} testId="chat-ref-preview-card" />
            <div className="px-2 text-[10px]" style={{ color: "var(--yalola-text-muted)" }}>{t("chat.ref_attached", "Jointe au prochain message")}</div>
          </div>
          <button onClick={() => setPendingRef(null)} className="h-6 w-6 self-center rounded-full grid place-items-center shrink-0 hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-text-muted)" }} aria-label={t("chat.ref_remove", "Retirer")} data-testid="chat-ref-cancel"><X size={14} /></button>
        </div>
      )}
      {replyTo && (
        <div className="px-2 pt-2 bg-white flex items-stretch gap-2" data-testid="chat-reply-preview">
          <div className="flex-1 min-w-0 rounded-lg border-l-4 px-2.5 py-1.5 flex items-center gap-2" style={{ borderColor: "var(--yalola-primary)", background: "var(--yalola-surface-alt)" }}>
            <QuoteThumb mediaType={replyTo.media_type} url={replyTo.media_url} size={36} testId="chat-reply-thumb" />
            <div className="min-w-0">
              <div className="text-[11px] font-bold" style={{ color: "var(--yalola-primary)" }}>
                {t("chat.replying_to", "Réponse à")} {replyTo.sender_id === user.id ? t("chat.you", "vous") : chatTarget.name}
              </div>
              <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{quoteLabel(replyTo, t)}</div>
            </div>
          </div>
          <button onClick={() => setReplyTo(null)} className="h-6 w-6 self-center rounded-full grid place-items-center shrink-0 hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-reply-cancel"><X size={14} /></button>
        </div>
      )}
      {editMsg && (
        <div className="px-2 pt-2 bg-white flex items-stretch gap-2" data-testid="chat-edit-preview">
          <div className="flex-1 min-w-0 rounded-lg border-l-4 px-2.5 py-1.5 flex items-center gap-2" style={{ borderColor: "var(--yalola-accent, #E0A63C)", background: "var(--yalola-surface-alt)" }}>
            <Pencil size={15} style={{ color: "var(--yalola-accent, #E0A63C)" }} className="shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] font-bold" style={{ color: "var(--yalola-accent, #E0A63C)" }}>{t("chat.editing", "Modifier le message")}</div>
              <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{editMsg.content}</div>
            </div>
          </div>
          <button onClick={cancelEdit} className="h-6 w-6 self-center rounded-full grid place-items-center shrink-0 hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-text-muted)" }} data-testid="chat-edit-cancel"><X size={14} /></button>
        </div>
      )}

      <div className="p-2 border-t flex items-end gap-1 bg-white" style={{ borderColor: "var(--yalola-border)", paddingBottom: isMobile ? "calc(0.5rem + var(--sa-bottom))" : undefined }} {...profileGateProps(user)} data-testid="chat-dock-composer">
        <input ref={fileRef} type="file" accept={(config?.settings?.media_limits?.chat_video_enabled !== false) ? "image/*,video/*" : "image/*"} multiple className="hidden" onChange={pickFile} data-testid="chat-dock-file-input" />
        {micOnRec ? (
          <div className="flex-1 min-w-0 flex items-center gap-1.5" data-testid="chat-recording-bar">
            <button onClick={cancelRecording} className="h-9 w-9 rounded-full grid place-items-center shrink-0 hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "#e11d48" }} title={t("chat.rec_cancel", "Annuler")} data-testid="chat-rec-cancel"><Trash2 size={19} /></button>
            <div className="flex-1 min-w-0 h-9 rounded-full px-3 flex items-center gap-2" style={{ background: "var(--yalola-surface-alt)" }}>
              <span className="text-red-600 font-semibold text-xs tabular-nums shrink-0" data-testid="chat-rec-timer">{fmtRec(recSecs)}</span>
              <div className="flex-1 min-w-0 flex items-center gap-[3px] h-6 overflow-hidden">
                {REC_BARS.map((h, i) => (
                  <span key={i} className={`rec-wave-bar w-[3px] rounded-full shrink-0${recPaused ? " rec-paused" : ""}`} style={{ height: `${h}px`, background: "var(--yalola-primary)", animationDelay: `${i * 55}ms` }} />
                ))}
              </div>
            </div>
            <button onClick={togglePauseRecording} className="h-9 w-9 rounded-full grid place-items-center shrink-0 hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-text)" }} title={recPaused ? t("chat.rec_resume", "Reprendre") : t("chat.rec_pause", "Pause")} data-testid="chat-rec-pause">
              {recPaused ? <Mic size={18} /> : <Pause size={18} fill="currentColor" />}
            </button>
            <button onClick={() => finishRecording(true)} className="h-9 w-9 rounded-full grid place-items-center shrink-0 transition-transform active:scale-90" style={{ background: "#22c55e", color: "#fff" }} title={t("chat.send", "Envoyer")} data-testid="chat-rec-send"><Send size={16} /></button>
          </div>
        ) : (
          <>
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-9 w-9 rounded-full grid place-items-center shrink-0 hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-primary)" }} title={t("chat.attach", "Photo / vidéo")} data-testid="chat-dock-attach">
              {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            </button>
            <div className="relative shrink-0">
              <button type="button" {...keepFocusTap(() => setEmojiOpen((o) => !o))} className="h-9 w-9 rounded-full grid place-items-center hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: emojiOpen ? "var(--yalola-primary)" : "var(--yalola-accent, #E0A63C)" }} title="Emoji" data-testid="chat-dock-emoji"><Smile size={18} /></button>
              {emojiOpen && (
                <>
                  <div className="fixed inset-0 z-[90]" onClick={() => setEmojiOpen(false)} data-testid="chat-emoji-backdrop" />
                  <div className="absolute bottom-11 left-0 z-[91] w-64 p-2 rounded-2xl shadow-xl border bg-white" style={{ borderColor: "var(--yalola-border)" }}>
                    <EmojiTabsGrid cols={8} cell="h-7 w-7 text-lg" onPick={addEmoji} tapProps={(fn) => keepFocusTap(fn)} gridTestId="chat-emoji-grid" itemTestId={(e) => `chat-emoji-${e}`} />
                  </div>
                </>
              )}
            </div>
            <ChatTextarea
              ref={inputRef}
              className="flex-1 min-w-0 rounded-[18px] px-3.5 py-2 text-sm border outline-none focus:border-[color:var(--yalola-primary)]"
              style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}
              value={text}
              onChange={(e) => {
                typeText(e.target.value);
                const now = Date.now();
                if (chatTarget && wsRef.current?.readyState === 1 && now - lastTypingSent.current > 1500) {
                  lastTypingSent.current = now;
                  try { wsRef.current.send(JSON.stringify({ type: "typing", to: chatTarget.id })); } catch { /* noop */ }
                }
              }}
              onSend={send}
              placeholder={t("groupchat.write_ph", "Écris un message…")}
              data-testid="chat-dock-input"
            />
            {text.trim() ? (
              <button type="button" {...keepFocusTap(send)} disabled={sending} className="h-9 w-9 rounded-full grid place-items-center shrink-0 hover:opacity-90 disabled:opacity-40" style={{ background: "var(--yalola-primary)", color: "#fff" }} data-testid="chat-dock-send"><Send size={15} /></button>
            ) : (
              <button onClick={startRecording} disabled={uploading} className="h-9 w-9 rounded-full grid place-items-center shrink-0 hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "var(--yalola-primary)" }} title={t("chat.voice_max", "Message vocal (max {n} s)").replace("{n}", voiceMax)} data-testid="chat-dock-mic"><Mic size={18} /></button>
            )}
          </>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="fixed inset-0 z-[94] flex flex-col" style={{ background: "#0b141a" }} data-testid="chat-media-caption">
          <div className="flex items-center justify-between px-2 shrink-0 text-white" style={{ paddingTop: "calc(0.5rem + var(--sa-top))" }}>
            <button onClick={requestCaptionClose} className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10" data-testid="chat-caption-close"><X size={24} /></button>
            <div className="text-sm font-medium truncate px-2">{chatTarget.name}</div>
            <span className="w-10 shrink-0" />
          </div>
          <div className="flex-1 flex items-center justify-center overflow-hidden px-2 min-h-0 relative">
            {capCur && (capCur.media_type === "video"
              ? <video src={capCur.preview} poster={capCur.poster || NEUTRAL_POSTER} controls playsInline preload="metadata" className="max-h-full max-w-full object-contain rounded-lg" data-testid="chat-caption-video" />
              : <img src={capCur.preview} alt="" className="max-h-full max-w-full object-contain rounded-lg" data-testid="chat-caption-image" />)}
            {capCur?.failed && (
              <div className="absolute inset-0 grid place-items-center px-4" data-testid={`chat-caption-failed-${capCur.id}`}>
                <div className="flex flex-col items-center gap-3 rounded-2xl px-6 py-5" style={{ background: "rgba(0,0,0,0.62)" }}>
                  <AlertTriangle size={30} className="text-red-400" />
                  <div className="text-white text-sm font-medium text-center">{t("chat.upload_failed", "Échec de l'envoi du fichier")}</div>
                  <button onClick={() => retryAttachment(capCur)} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-transform active:scale-95" style={{ background: "#fff", color: "#0b141a" }} data-testid={`chat-caption-retry-${capCur.id}`}>
                    <RotateCw size={14} /> {t("chat.retry", "Réessayer")}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="px-2 py-2 flex items-center gap-2 overflow-x-auto shrink-0">
            {attachments.map((a, i) => (
              <button key={a.id} onClick={() => setCaptionIndex(i)} className="relative h-14 w-14 rounded-lg overflow-hidden shrink-0 border-2" style={{ borderColor: i === capIdx ? "var(--yalola-primary)" : "transparent" }} data-testid={`chat-caption-thumb-${a.id}`}>
                {a.media_type === "video"
                  ? (a.poster
                      ? <img src={a.poster} alt="" className="h-full w-full object-cover" />
                      : <video src={a.preview} poster={NEUTRAL_POSTER} muted className="h-full w-full object-cover" />)
                  : <img src={a.preview} alt="" className="h-full w-full object-cover" />}
                {a.uploading && (
                  <span className="absolute inset-0 bg-black/50 grid place-items-center">
                    {typeof a.progress === "number" && a.progress > 0 && a.progress < 100
                      ? <span className="text-white text-[10px] font-bold" data-testid={`chat-attach-progress-${a.id}`}>{a.progress}%</span>
                      : <Loader2 size={14} className="animate-spin text-white" />}
                  </span>
                )}
                {a.failed && (
                  <span onClick={(e) => { e.stopPropagation(); retryAttachment(a); }} className="absolute inset-0 grid place-items-center cursor-pointer" style={{ background: "rgba(220,38,38,0.55)" }} data-testid={`chat-attach-retry-${a.id}`}>
                    <RotateCw size={16} className="text-white" />
                  </span>
                )}
                <span onClick={(e) => { e.stopPropagation(); removeAttachment(a.id); }} className="absolute top-0 right-0 h-4 w-4 bg-black/70 grid place-items-center cursor-pointer" data-testid={`chat-caption-remove-${a.id}`}><X size={9} className="text-white" /></span>
              </button>
            ))}
            <button onClick={() => fileRef.current?.click()} className="h-14 w-14 rounded-lg border-2 border-dashed grid place-items-center shrink-0 text-white/70" style={{ borderColor: "rgba(255,255,255,0.3)" }} data-testid="chat-caption-add-more"><ImagePlus size={20} /></button>
          </div>
          <div className="flex items-end gap-2 px-2 pb-3 shrink-0" style={{ paddingBottom: "calc(0.75rem + var(--sa-bottom))" }}>
            <ChatTextarea value={text} onChange={(e) => typeText(e.target.value)} onSend={send} placeholder={t("chat.add_caption", "Ajouter une légende…")} className="flex-1 min-w-0 rounded-[22px] px-4 py-3 text-sm text-white outline-none placeholder:text-white/60" style={{ background: "rgba(255,255,255,0.1)" }} data-testid="chat-caption-input" />
            <button onClick={send} disabled={sending} className="h-11 w-11 rounded-full grid place-items-center shrink-0 relative disabled:opacity-50" style={{ background: "var(--yalola-primary)", color: "#fff" }} data-testid="chat-caption-send">
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {attachments.length > 1 && (
                <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-white text-[11px] font-bold grid place-items-center" style={{ color: "var(--yalola-primary)" }}>{attachments.length}</span>
              )}
            </button>
          </div>
          {confirmClose && (
            <div className="absolute inset-0 z-[96] grid place-items-center px-6" style={{ background: "rgba(0,0,0,0.62)" }} data-testid="chat-caption-close-confirm">
              <div className="w-full max-w-xs rounded-2xl bg-white p-4 text-center" style={{ color: "var(--yalola-text)" }}>
                <div className="text-sm font-semibold mb-1">{t("chat.discard_failed_title", "Abandonner l'envoi ?")}</div>
                <div className="text-xs mb-4" style={{ color: "var(--yalola-text-muted)" }}>{t("chat.discard_failed_body", "Une pièce jointe n'a pas pu être envoyée. Si tu fermes, elle sera perdue.")}</div>
                <div className="flex gap-2">
                  <button onClick={() => setConfirmClose(false)} className="flex-1 h-9 rounded-full text-sm font-semibold border" style={{ borderColor: "var(--yalola-border)" }} data-testid="chat-caption-close-cancel">{t("common.cancel", "Annuler")}</button>
                  <button onClick={discardCaption} className="flex-1 h-9 rounded-full text-sm font-semibold text-white" style={{ background: "#e11d48" }} data-testid="chat-caption-close-discard">{t("chat.discard", "Fermer")}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
});
